<template>
    <div class="dashboard tk-detail-page">
        <main class="tk-detail-content">
            <q-banner v-if="error" rounded class="bg-red-1 text-negative q-ma-md">
                {{ error }}
            </q-banner>

            <template v-if="engagement">
                <header class="tk-topbar">
                    <div class="tk-topbar__left">
                        <q-btn flat round dense icon="arrow_back" @click="$router.back()">
                            <q-tooltip class="text-body2">Back</q-tooltip>
                        </q-btn>
                        <nav class="tk-breadcrumb" aria-label="Breadcrumb">
                            <button type="button" @click="goToClients">Clients</button>
                            <q-icon name="chevron_right" size="18px" />
                            <span>{{ engagement.personName }}</span>
                        </nav>
                    </div>
                </header>

                <TkEngagementWorkspace
                    v-model="activePanel"
                    :engagement="engagement"
                    :case-note-count="caseNoteCount"
                    :insights-accessible="canAccessInsights"
                >
                    <template #actions>
                        <q-btn
                            v-if="activePanel === 'overview' && canEditWhaioraStatus"
                            flat
                            dense
                            no-caps
                            color="primary"
                            label="Change status"
                            @click="showStatusDialog = true"
                        />
                    </template>

                    <template #overview>
                <section class="tk-page-grid">
                    <section class="tk-main-column">
                        <TkEngagementInformationSnapshot
                            :engagement="engagement"
                            @open="activePanel = 'client-information'"
                        />

                        <section v-if="canAccessInsights" class="tk-card">
                            <TkEngagementInsightSummary
                                ref="insightSummary"
                                :person-id="engagement.tk_person_id"
                                :engagement-id="engagement.id"
                                @open="openInsights"
                            />
                        </section>

                        <section class="tk-card">
                            <div class="tk-card-header">
                                <h2>Case Notes</h2>
                                <q-btn v-if="canCreateCaseNotes" unelevated dense no-caps flat color="primary" icon="add" label="Add note" @click="openNewCaseNote" />
                            </div>
                            <TkCaseNotesPanel
                                ref="caseNotesFeed"
                                :person-id="engagement.tk_person_id"
                                :engagement-id="engagement.id"
                                :staff-options="staffOptions"
                                :current-staff-id="currentStaffId"
                                :assigned-worker-id="engagement.assigned_worker"
                                :show-header="false"
                                :show-form="false"
                                :show-list="true"
                                :use-card="false"
                                :note-limit="3"
                                @count-changed="caseNoteCount = $event"
                                @edit="openCaseNoteEdit"
                            />
                            <q-btn
                                class="q-mt-sm"
                                flat
                                dense
                                no-caps
                                color="primary"
                                label="View full history & search →"
                                @click="openCaseNoteHistory"
                            />
                        </section>

                        <section class="tk-card">
                            <div class="tk-card-header">
                                <h2>Group Attendance</h2>
                            </div>
                            <q-table
                                :rows="engagement.group_attendance || []"
                                :columns="groupAttendanceColumns"
                                row-key="group_member_id"
                                flat
                                dense
                                hide-pagination
                                :rows-per-page-options="[0]"
                                class="client-table"
                            >
                                <template v-slot:body-cell-group="props">
                                    <q-td :props="props">
                                        {{ props.row.group_name }}
                                        <q-badge
                                            v-if="!props.row.membership_is_active"
                                            outline
                                            color="grey-7"
                                            label="Past member"
                                            class="q-ml-sm"
                                        />
                                    </q-td>
                                </template>
                                <template v-slot:body-cell-attendance="props">
                                    <q-td :props="props">
                                        <div
                                            v-if="groupAttendancePercentage(props.row) != null"
                                            class="tk-attendance-progress"
                                        >
                                            <q-linear-progress
                                                :value="groupAttendancePercentage(props.row) / 100"
                                                :color="groupAttendanceProgressColor(props.row)"
                                                track-color="grey-3"
                                                rounded
                                                size="12px"
                                                class="tk-attendance-progress__bar"
                                                :aria-label="`Overall attendance ${groupAttendancePercentageLabel(props.row)}`"
                                            />
                                            <span class="tk-attendance-progress__label">
                                                {{ groupAttendancePercentageLabel(props.row) }}
                                            </span>
                                        </div>
                                        <span v-else>-</span>
                                    </q-td>
                                </template>
                                <template v-slot:no-data>
                                    <div class="full-width row flex-center text-grey-7 q-pa-md">
                                        This Client Engagement is not linked to any groups.
                                    </div>
                                </template>
                            </q-table>
                        </section>

                        <section class="tk-card">
                            <TkEngagementFormsPanel
                                :engagement="engagement"
                                @goals-changed="$refs.engagementGoals?.refreshGoals()"
                            />
                        </section>

                        <section class="tk-card relative-position">
                            <div class="tk-card-header">
                                <h2>Goals</h2>
                            </div>
                            <GoalTable
                                ref="engagementGoals"
                                :person-id="engagement.tk_person_id"
                                :engagement-id="engagement.id"
                            />
                        </section>

                    </section>

                    <aside class="tk-aside-column">
                        <section class="tk-side-card">
                            <div class="tk-card-header tk-card-header--compact">
                                <h2>Engagement Status</h2>
                            </div>
                            <div class="tk-current-status">
                                <TkContextBadge kind="status" :value="engagement.status_code" :label="engagement.statusLabel" />
                                <span>Since {{ statusSince }}</span>
                            </div>
                            <div class="tk-timeline">
                                <div
                                    v-for="item in lifecycleTimeline"
                                    :key="item.label"
                                    :class="['tk-timeline-item', { 'tk-timeline-item--complete': item.complete }]"
                                >
                                    <span class="tk-timeline-dot" />
                                    <div>
                                        <div class="tk-timeline-heading">
                                            <strong>{{ item.label }}</strong>
                                            <span>{{ item.worker }}</span>
                                        </div>
                                        <span>{{ item.date }}</span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section class="tk-side-card">
                            <div class="tk-card-header tk-card-header--compact">
                                <h2>Assigned Workers</h2>
                            </div>
                            <div class="tk-worker-group">
                                <span class="tk-worker-group__label">Primary</span>
                                <div class="tk-worker-card">
                                    <q-avatar color="secondary" text-color="white" size="38px">
                                        {{ initials(engagement.assignedWorkerName) }}
                                    </q-avatar>
                                    <div>
                                        <strong>{{ engagement.assignedWorkerName }}</strong>
                                    </div>
                                </div>
                            </div>
                            <q-separator class="q-my-md" />
                            <div class="tk-worker-group">
                                <span class="tk-worker-group__label">Additional</span>
                                <div v-if="engagement.additionalWorkers.length" class="column q-gutter-sm">
                                    <div
                                        v-for="worker in engagement.additionalWorkers"
                                        :key="worker.id || worker.staff_id"
                                        class="tk-worker-card"
                                    >
                                        <q-avatar color="secondary" text-color="white" size="38px">
                                            {{ initials(worker.workerName) }}
                                        </q-avatar>
                                        <div>
                                            <strong>{{ worker.workerName }}</strong>
                                        </div>
                                    </div>
                                </div>
                                <div v-else class="tk-checklist-empty">No additional workers assigned.</div>
                            </div>
                        </section>

                        <section class="tk-side-card">
                            <div class="tk-card-header tk-card-header--compact">
                                <h2>Checklist &amp; Alerts</h2>
                            </div>
                            <div v-if="engagement.checklist_items && engagement.checklist_items.length" class="tk-checklist-list">
                                <q-checkbox
                                    v-for="item in engagement.checklist_items"
                                    :key="item.id"
                                    :model-value="!!item.is_completed"
                                    :label="item.label"
                                    dense
                                    color="primary"
                                    :disable="checklistSavingId === item.id"
                                    @update:model-value="setChecklistItemCompletion(item, $event)"
                                />
                            </div>
                            <div v-else class="tk-checklist-empty">
                                No checklist items required.
                            </div>
                            <q-separator class="q-my-md" />
                            <div class="tk-engagement-detail-row">
                                <span>Risk Assessment</span>
                                <strong>{{ riskAssessmentLabels || "None" }}</strong>
                            </div>
                            <div class="tk-engagement-detail-row tk-engagement-detail-row--last">
                                <span>Active Reminder</span>
                                <strong>{{ engagement.activeReminder?.dueLabel || "Needs tier required" }}</strong>
                            </div>
                        </section>
                    </aside>
                </section>
                    </template>

                    <template #client-information>
                        <TkEngagementInformationPanel
                            :engagement="engagement"
                            @edit-person="showPersonDialog = true"
                            @edit-client="showDetailsDialog = true"
                            :can-edit-status="canEditWhaioraStatus"
                            @edit-status="showStatusDialog = true"
                        />
                    </template>

                    <template #case-notes>
                        <TkCaseNotesHistoryPanel
                            ref="caseNotesHistory"
                            :can-create="canCreateCaseNotes"
                            :person-id="engagement.tk_person_id"
                            :engagement-id="engagement.id"
                            @add="openNewCaseNote"
                            @count-changed="caseNoteCount = $event"
                            @edit="openCaseNoteEdit"
                        />
                    </template>

                    <template #insights>
                        <TkEngagementInsightsPanel
                            v-if="canAccessInsights"
                            :person-id="engagement.tk_person_id"
                            :engagement-id="engagement.id"
                            @changed="$refs.insightSummary?.refresh()"
                        />
                    </template>

                    <template #attachments>
                        <TkAttachmentsPanel
                            ref="attachmentsPanel"
                            owner-type="engagement"
                            :owner-id="engagement.id"
                            @add="showDocumentDialog = true"
                        />
                    </template>
                </TkEngagementWorkspace>
            </template>

            <section v-else-if="!loading && !error" class="tk-empty-state">
                <q-icon name="o_folder_open" size="40px" />
                <div>
                    <h2>Client Engagement not found</h2>
                    <p>The record may have moved or you may not have access.</p>
                </div>
            </section>

            <q-inner-loading :showing="loading" />
        </main>

        <TkPersonDialog
            v-if="person"
            v-model="showPersonDialog"
            :person="person"
            @updated="onPersonUpdated"
        />

        <TkRightDrawer
            v-if="engagement && (editingCaseNote ? canEditCaseNotes : canCreateCaseNotes)"
            v-model="showCaseNoteDialog"
            :title="editingCaseNote ? 'Edit Case Note' : 'Add Case Note'"
            subtitle="Record an interaction for this client engagement."
            width="900px"
            @hide="onCaseNoteDrawerHide"
        >
            <q-card-section>
                <TkCaseNotesPanel
                    :key="`${editingCaseNote?.id || 'new-case-note'}-${caseNoteDrawerKey}`"
                    ref="caseNoteDialog"
                    :person-id="engagement.tk_person_id"
                    :engagement-id="engagement.id"
                    :staff-options="staffOptions"
                    :current-staff-id="currentStaffId"
                    :assigned-worker-id="engagement.assigned_worker"
                    :show-header="false"
                    :show-form="true"
                    :show-list="false"
                    :use-card="false"
                    :note-to-edit="editingCaseNote"
                    :can-create="canCreateCaseNotes"
                    :can-edit="canEditCaseNotes"
                    @created="onCaseNoteCreated"
                    @updated="onCaseNoteUpdated"
                    @goals-created="onGoalsCreated"
                />
            </q-card-section>
        </TkRightDrawer>

        <q-dialog v-if="engagement" v-model="showDocumentDialog">
            <q-card class="tk-dialog-card" style="width: 900px; max-width: 96vw;">
                <q-card-section class="tk-dialog-header">
                    <h2>New Attachment</h2>
                    <q-btn flat round dense icon="close" v-close-popup>
                        <q-tooltip class="text-body2">Close</q-tooltip>
                    </q-btn>
                </q-card-section>
                <q-card-section>
                    <TkDocumentsPanel
                        owner-type="engagement"
                        :owner-id="engagement.id"
                        :show-upload="true"
                        :show-list="false"
                        @uploaded="onDocumentUploaded"
                    />
                </q-card-section>
            </q-card>
        </q-dialog>

        <TkRightDrawer
            v-if="engagement"
            v-model="showDetailsDialog"
            title="Client Engagement Details"
            subtitle="Update the service, workers, risk assessment, and checklist."
            width="900px"
            @hide="resetForms"
        >
                <q-form ref="detailsForm" @submit.prevent="saveDetailsDialog">
                    <q-card-section>
                        <div class="tk-form-grid">
                            <div class="tk-field">
                                <p><strong>Service</strong></p>
                                <q-select v-model="detailForm.serviceCode" :options="serviceOptions" outlined dense emit-value map-options />
                            </div>
                            <div class="tk-field tk-field--half">
                                <p><strong>Referral Source</strong></p>
                                <q-select
                                    v-model="detailForm.referralSource"
                                    :options="referralSourceOptions"
                                    outlined
                                    dense
                                    clearable
                                    emit-value
                                    map-options
                                />
                            </div>
                            <div class="tk-field">
                                <p><strong>Primary Worker</strong></p>
                                <q-select v-model="detailForm.assignedWorker" :options="staffOptions" outlined dense clearable emit-value map-options />
                            </div>

                            <div class="tk-field tk-field--half">
                                <p><strong>Additional Workers</strong></p>
                                <q-select v-model="detailForm.additionalWorkers" :options="staffOptions" outlined dense clearable emit-value map-options multiple use-chips />
                            </div>
                            <div class="tk-field tk-field--half">
                                <p><strong>Needs</strong></p>
                                <q-select
                                    v-model="detailForm.riskFactor"
                                    :options="needsOptions"
                                    outlined
                                    dense
                                    clearable
                                    emit-value
                                    map-options
                                />
                            </div>
                            <div class="tk-field">
                                <p><strong>Entry Date</strong></p>
                                <TkDateField v-model="detailForm.enteredDate" :rules="enteredDateRules" />
                            </div>
                            <div class="tk-field tk-field--wide">
                                <p><strong>Risk Assessment</strong></p>
                                <TkOptionPillGroup
                                    v-model="detailForm.riskTypeOptionIds"
                                    :options="riskAssessmentOptions"
                                />
                            </div>
                            <div class="tk-field">
                                <p><strong>Risk Assessment Date</strong></p>
                                <TkDateField
                                    v-model="detailForm.riskAssessmentDate"
                                    :rules="riskAssessmentDateRules"
                                />
                            </div>
                            <div class="tk-field tk-field--wide">
                                <p><strong>Risk Assessment Notes</strong></p>
                                <q-input
                                    v-model="detailForm.riskAssessmentNotes"
                                    outlined
                                    dense
                                    clearable
                                    type="textarea"
                                    rows="3"
                                    maxlength="2000"
                                />
                            </div>
                            <div class="tk-field">
                                <p><strong>Active Reminder Last Reviewed</strong></p>
                                <TkDateField
                                    v-model="detailForm.activeReminderReviewedDate"
                                    :disable="!detailForm.riskFactor"
                                />
                            </div>
                        </div>
                        <div class="tk-dialog-checklist">
                            <p><strong>Required Checklist Items</strong></p>
                            <q-option-group
                                v-model="detailForm.checklistDefinitionIds"
                                :options="checklistOptions"
                                type="checkbox"
                                dense
                                color="primary"
                            />
                            <div class="tk-custom-checklist row q-col-gutter-sm q-mt-sm">
                                <div class="col">
                                    <q-input
                                        v-model="detailCustomChecklistLabel"
                                        outlined
                                        dense
                                        clearable
                                        label="Custom checklist item"
                                        @keyup.enter.stop.prevent="addDetailCustomChecklistItem"
                                    />
                                </div>
                                <div class="col-auto">
                                    <q-btn
                                        outline
                                        no-caps
                                        color="primary"
                                        label="Add"
                                        :disable="!String(detailCustomChecklistLabel || '').trim()"
                                        @click="addDetailCustomChecklistItem"
                                    />
                                </div>
                            </div>
                            <div v-if="detailForm.customChecklistLabels.length" class="tk-custom-chip-row">
                                <q-chip
                                    v-for="label in detailForm.customChecklistLabels"
                                    :key="label"
                                    removable
                                    dense
                                    color="grey-2"
                                    text-color="dark"
                                    @remove="removeDetailCustomChecklistItem(label)"
                                >
                                    {{ label }}
                                </q-chip>
                            </div>
                        </div>
                    </q-card-section>
                    <q-card-actions align="right">
                        <q-btn flat no-caps color="negative" label="Cancel" v-close-popup />
                        <q-btn unelevated no-caps color="primary" icon="save" label="Save" type="submit" :loading="saving" />
                    </q-card-actions>
                </q-form>
        </TkRightDrawer>

        <TkRightDrawer
            v-if="engagement"
            v-model="showStatusDialog"
            title="Edit Status"
            subtitle="Update this client engagement's lifecycle status."
            width="900px"
            @hide="resetForms"
        >
                <q-card-section>
                    <div class="tk-form-grid tk-form-grid--status">
                        <div class="tk-field">
                            <p><strong>Status</strong></p>
                            <q-select v-model="lifecycleEditForm.statusCode" :options="allowedLifecycleOptions" outlined dense emit-value map-options />
                        </div>
                        <div class="tk-field">
                            <p><strong>Activation Date</strong></p>
                            <TkDateField
                                v-model="lifecycleEditForm.activatedDate"
                                :disable="!showLifecycleActivationFields"
                                :rules="activatedDateRules"
                            />
                        </div>
                        <div class="tk-field">
                            <p><strong>Exit Date</strong></p>
                            <TkDateField
                                v-model="lifecycleEditForm.exitedDate"
                                :disable="!showLifecycleExitFields"
                                :rules="exitedDateRules"
                            />
                        </div>
                        <div class="tk-field">
                            <p><strong>Exit Reason</strong></p>
                            <q-select
                                v-model="lifecycleEditForm.exitCode"
                                :options="exitCodeOptions"
                                outlined
                                dense
                                clearable
                                emit-value
                                map-options
                                :disable="!showLifecycleExitFields"
                            />
                        </div>
                        <div class="tk-field">
                            <p><strong>Referred To</strong></p>
                            <q-select
                                v-model="lifecycleEditForm.referredTo"
                                :options="exitReferralOptions"
                                outlined
                                dense
                                clearable
                                emit-value
                                map-options
                                :disable="!showLifecycleExitFields"
                            />
                        </div>
                        <div class="tk-field">
                            <p><strong>Signed Off By</strong></p>
                            <q-select
                                v-model="lifecycleEditForm.signedOffBy"
                                :options="staffOptions"
                                outlined
                                dense
                                clearable
                                emit-value
                                map-options
                                :disable="!showLifecycleExitFields"
                            />
                        </div>
                        <div class="tk-field">
                            <p><strong>Sign-off Date</strong></p>
                            <TkDateField
                                v-model="lifecycleEditForm.signedOffDate"
                                :disable="!showLifecycleExitFields"
                                :rules="signedOffDateRules"
                            />
                        </div>
                    </div>
                </q-card-section>
                <q-card-actions align="right">
                    <q-btn flat no-caps color="negative" label="Cancel" v-close-popup />
                    <q-btn
                        unelevated
                        no-caps
                        color="primary"
                        icon="save"
                        :label="lifecycleSaveLabel"
                        :loading="saving"
                        :disable="lifecycleEditInvalid"
                        @click="saveLifecycleDialog"
                    />
                </q-card-actions>
        </TkRightDrawer>
    </div>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import token from "@/services/util/roleHandler";
import Roles from "@/services/util/roles";
import TkDateField from "@/components/TkClients/TkDateField.vue";
import TkAttachmentsPanel from "@/components/TkClients/TkAttachmentsPanel.vue";
import TkCaseNotesPanel from "@/components/TkClients/TkCaseNotesPanel.vue";
import TkCaseNotesHistoryPanel from "@/components/TkClients/TkCaseNotesHistoryPanel.vue";
import TkContextBadge from "@/components/TkClients/TkContextBadge.vue";
import TkEngagementWorkspace from "@/components/TkClients/TkEngagementWorkspace.vue";
import TkEngagementInformationPanel from "@/components/TkClients/TkEngagementInformationPanel.vue";
import TkEngagementInformationSnapshot from "@/components/TkClients/TkEngagementInformationSnapshot.vue";
import TkEngagementInsightSummary from "@/components/TkClients/TkEngagementInsightSummary.vue";
import TkEngagementInsightsPanel from "@/components/TkClients/TkEngagementInsightsPanel.vue";
import TkDocumentsPanel from "@/components/TkClients/TkDocumentsPanel.vue";
import TkEngagementFormsPanel from "@/components/TkClients/TkEngagementFormsPanel.vue";
import TkOptionPillGroup from "@/components/TkClients/TkOptionPillGroup.vue";
import TkRightDrawer from "@/components/TkClients/TkRightDrawer.vue";
import GoalTable from "@/components/GoalComponents/GoalTable.vue";
import TkPersonDialog from "@/components/ClientComponents/TemporaryClients/TkPersonDialog.vue";
import {
    groupAttendancePercentage,
    groupAttendancePercentageLabel,
    groupAttendanceProgressColor,
    groupAttendanceSessionLabel,
    mapEngagementRow,
    personFromEngagement,
    needsTierOptions,
    staffOption,
    toDDMMYYYY,
    toSubmitDate,
} from "@/services/util/tkClients";

const STATUS_ORDER = { waitlist: 1, active: 2, exited: 3 };

export default {
    name: "TkEngagementDetail",
    components: {
        GoalTable,
        TkDateField,
        TkAttachmentsPanel,
        TkCaseNotesPanel,
        TkCaseNotesHistoryPanel,
        TkContextBadge,
        TkEngagementInformationPanel,
        TkEngagementInformationSnapshot,
        TkEngagementInsightSummary,
        TkEngagementInsightsPanel,
        TkEngagementWorkspace,
        TkDocumentsPanel,
        TkEngagementFormsPanel,
        TkOptionPillGroup,
        TkRightDrawer,
        TkPersonDialog,
    },
    data() {
        return {
            loading: false,
            saving: false,
            error: null,
            engagement: null,
            lookups: {
                services: [],
                statuses: [],
                referral_sources: [],
                exit_codes: [],
                exit_referrals: [],
                checklist_item_definitions: [],
                risk_types: [],
            },
            staff: [],
            groupAttendanceColumns: [
                { name: "group", label: "Group", field: "group_name", align: "left", sortable: true },
                {
                    name: "sessions",
                    label: "Sessions attended",
                    field: row => Number(row.sessions_attended) || 0,
                    format: (value, row) => groupAttendanceSessionLabel(row),
                    align: "right",
                    sortable: true,
                },
                {
                    name: "attendance",
                    label: "Overall attendance",
                    field: row => row.attendance_percentage == null
                        ? -1
                        : Number(row.attendance_percentage),
                    format: (value, row) => groupAttendancePercentageLabel(row),
                    align: "right",
                    sortable: true,
                },
            ],
            checklistSavingId: null,
            detailCustomChecklistLabel: "",
            detailForm: {
                serviceCode: null,
                assignedWorker: null,
                additionalWorkers: [],
                referralSource: null,
                riskFactor: null,
                riskTypeOptionIds: [],
                riskAssessmentDate: "",
                riskAssessmentNotes: "",
                activeReminderReviewedDate: "",
                enteredDate: "",
                checklistDefinitionIds: [],
                customChecklistLabels: [],
            },
            lifecycleEditForm: {
                statusCode: null,
                activatedDate: "",
                exitedDate: "",
                exitCode: null,
                referredTo: null,
                signedOffBy: null,
                signedOffDate: "",
            },
            showPersonDialog: false,
            showCaseNoteDialog: false,
            activePanel: "overview",
            caseNoteCount: 0,
            editingCaseNote: null,
            caseNoteDrawerKey: 0,
            showDocumentDialog: false,
            showDetailsDialog: false,
            showStatusDialog: false,
            ownPermissions: {},
        };
    },
    computed: {
        canCreateCaseNotes() {
            return !!this.ownPermissions.create_case_notes;
        },
        canEditCaseNotes() {
            return !!this.ownPermissions.edit_case_notes;
        },
        canEditWhaioraStatus() {
            return !!this.ownPermissions.edit_whaiora_status;
        },
        person() {
            return this.engagement ? personFromEngagement(this.engagement) : null;
        },
        lifecycleTimeline() {
            return [
                {
                    label: "Entered",
                    worker: this.engagement.created_by_name || "-",
                    date: this.engagement.entered_at ? this.engagement.enteredAt : "Not yet",
                    complete: !!this.engagement.entered_at,
                },
                {
                    label: "Activated",
                    worker: this.engagement.activatedByName,
                    date: this.engagement.activated_at ? this.engagement.activatedAt : "Not yet",
                    complete: !!this.engagement.activated_at,
                },
                {
                    label: "Exited",
                    worker: this.engagement.signedOffByName || this.engagement.updated_by_name || "-",
                    date: this.engagement.exited_at ? this.engagement.exitedAt : "Not yet",
                    complete: !!this.engagement.exited_at,
                },
            ];
        },
        statusSince() {
            if (this.engagement.status_code === "exited") return this.engagement.exitedAt;
            if (this.engagement.status_code === "active") return this.engagement.activatedAt;
            return this.engagement.enteredAt;
        },
        serviceOptions() {
            return this.lookups.services.map(service => ({ label: service.name, value: service.code }));
        },
        referralSourceOptions() {
            return (this.lookups.referral_sources || []).map(source => ({ label: `${source.code} - ${source.name}`, value: source.code }));
        },
        exitCodeOptions() {
            return this.lookups.exit_codes.map(code => ({ label: code.name, value: code.code }));
        },
        exitReferralOptions() {
            return this.lookups.exit_referrals.map(code => ({ label: code.name, value: code.code }));
        },
        staffOptions() {
            return this.staff.map(staffOption);
        },
        needsOptions() {
            return needsTierOptions;
        },
        riskAssessmentLabels() {
            return (this.engagement?.risk_assessments || [])
                .map(risk => risk.label)
                .join(", ");
        },
        noRisksOptionId() {
            const option = (this.lookups.risk_types || [])
                .find(riskType => riskType.key === "no_risks_identified");
            return option ? String(option.id) : null;
        },
        riskAssessmentOptions() {
            return (this.lookups.risk_types || []).map(option => ({
                label: option.label,
                value: String(option.id),
            }));
        },
        checklistOptions() {
            return (this.lookups.checklist_item_definitions || []).map(item => ({
                label: item.label,
                value: item.id,
            }));
        },
        statusOptions() {
            return this.lookups.statuses.map(status => ({ label: status.name, value: status.code }));
        },
        allowedLifecycleOptions() {
            const currentOrder = STATUS_ORDER[this.engagement?.status_code] || 1;
            return this.statusOptions.filter(option => (STATUS_ORDER[option.value] || 0) >= currentOrder);
        },
        showLifecycleActivationFields() {
            return ["active", "exited"].includes(this.lifecycleEditForm.statusCode);
        },
        showLifecycleExitFields() {
            return this.lifecycleEditForm.statusCode === "exited";
        },
        lifecycleSaveLabel() {
            if (this.engagement?.status_code === "waitlist" && this.lifecycleEditForm.statusCode === "active") {
                return "Activate Client Engagement";
            }
            if (this.engagement?.status_code !== "exited" && this.lifecycleEditForm.statusCode === "exited") {
                return "Exit Client Engagement";
            }
            return "Save Status";
        },
        lifecycleEditInvalid() {
            if (!this.lifecycleEditForm.statusCode) return true;
            if (this.showLifecycleActivationFields && !this.lifecycleEditForm.activatedDate) return true;
            if (this.showLifecycleExitFields && (!this.lifecycleEditForm.exitedDate || !this.lifecycleEditForm.exitCode)) return true;
            const hasSignOffBy = !!this.lifecycleEditForm.signedOffBy;
            const hasSignOffDate = !!this.lifecycleEditForm.signedOffDate;
            return hasSignOffBy !== hasSignOffDate
                || !this.isDateOnOrAfter(this.lifecycleEditForm.activatedDate, this.engagementEnteredDate)
                || !this.isDateOnOrAfter(this.lifecycleEditForm.exitedDate, this.lifecycleEditForm.activatedDate)
                || !this.isDateOnOrAfter(this.lifecycleEditForm.signedOffDate, this.lifecycleEditForm.exitedDate);
        },
        currentStaffId() {
            const email = token.getEmail();
            const currentStaff = this.staff.find(staff => String(staff.email || "").toLowerCase() === String(email || "").toLowerCase());
            return currentStaff?.id || null;
        },
        canAccessInsights() {
            return [Roles.TeamLeader, Roles.Admin, Roles.Developer]
                .includes(token.getRole());
        },
        engagementEnteredDate() {
            return toDDMMYYYY(this.engagement?.entered_at);
        },
        engagementActivatedDate() {
            return toDDMMYYYY(this.engagement?.activated_at);
        },
        engagementExitedDate() {
            return toDDMMYYYY(this.engagement?.exited_at);
        },
        enteredDateRules() {
            return [
                value => this.isDateOnOrBefore(value, this.engagementActivatedDate)
                    || "Entry Date cannot be after Activation Date.",
                value => this.isDateOnOrBefore(value, this.engagementExitedDate)
                    || "Entry Date cannot be after Exit Date.",
            ];
        },
        activatedDateRules() {
            return [
                value => this.isDateOnOrAfter(value, this.engagementEnteredDate)
                    || "Activation Date cannot be before Entry Date.",
            ];
        },
        exitedDateRules() {
            return [
                value => this.isDateOnOrAfter(value, this.lifecycleEditForm.activatedDate)
                    || "Exit Date cannot be before Activation Date.",
            ];
        },
        signedOffDateRules() {
            return [
                value => this.isDateOnOrAfter(value, this.lifecycleEditForm.exitedDate)
                    || "Sign-off Date cannot be before Exit Date.",
            ];
        },
        riskAssessmentDateRules() {
            return [
                value => !this.detailForm.riskTypeOptionIds.length
                    || !!value
                    || "Risk Assessment Date is required.",
            ];
        },
    },
    async mounted() {
        await this.loadPage();
    },
    watch: {
        "detailForm.riskTypeOptionIds"(value, previousValue) {
            const noRisksOptionId = this.noRisksOptionId;
            const selectedIds = (value || []).map(String);
            const previousIds = (previousValue || []).map(String);
            if (!noRisksOptionId || !selectedIds.includes(noRisksOptionId) || selectedIds.length === 1) return;

            const noRisksWasAdded = !previousIds.includes(noRisksOptionId);
            this.detailForm.riskTypeOptionIds = noRisksWasAdded
                ? [noRisksOptionId]
                : selectedIds.filter(optionId => optionId !== noRisksOptionId);
        },
        "lifecycleEditForm.statusCode"(statusCode, previousStatusCode) {
            if (statusCode === previousStatusCode) return;
            if (this.showLifecycleActivationFields && !this.lifecycleEditForm.activatedDate) {
                this.lifecycleEditForm.activatedDate = toDDMMYYYY(new Date());
            }
            if (this.showLifecycleExitFields && !this.lifecycleEditForm.exitedDate) {
                this.lifecycleEditForm.exitedDate = toDDMMYYYY(new Date());
            }
        },
    },
    methods: {
        groupAttendancePercentage,
        groupAttendancePercentageLabel,
        groupAttendanceProgressColor,
        initials(value) {
            const text = String(value || "").trim();
            if (!text) return "-";
            return text.split(/\s+/).slice(0, 2).map(part => part[0]).join("").toUpperCase();
        },
        additionalWorkerPayload() {
            const existingByStaffId = new Map(
                this.engagement.additionalWorkers.map(worker => [worker.staff_id, worker])
            );
            return (this.detailForm.additionalWorkers || []).map(staffId => ({
                staff_id: staffId,
                engagement_role: existingByStaffId.get(staffId)?.engagement_role || null,
            }));
        },
        goToClients() {
            this.$router.push({ name: "tkClients" });
        },
        dateSortValue(value) {
            const parts = String(value || "").split("/").map(Number);
            if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
            const [day, month, year] = parts;
            return (year * 10000) + (month * 100) + day;
        },
        isDateOnOrBefore(value, comparisonValue) {
            const dateValue = this.dateSortValue(value);
            const comparisonDateValue = this.dateSortValue(comparisonValue);
            return dateValue === null || comparisonDateValue === null || dateValue <= comparisonDateValue;
        },
        isDateOnOrAfter(value, comparisonValue) {
            const dateValue = this.dateSortValue(value);
            const comparisonDateValue = this.dateSortValue(comparisonValue);
            return dateValue === null || comparisonDateValue === null || dateValue >= comparisonDateValue;
        },
        submitDateOrExisting(value, existingValue) {
            if (value === toDDMMYYYY(existingValue)) return existingValue;
            return toSubmitDate(value);
        },
        async loadPage() {
            this.loading = true;
            this.error = null;
            try {
                await Promise.all([this.loadLookups(), this.loadStaff(), this.loadPermissions()]);
                await this.loadEngagement();
            } catch (error) {
                this.error = "Failed to load Client Engagement.";
            } finally {
                this.loading = false;
            }
        },
        async loadPermissions() {
            this.ownPermissions = {};
            const res = await api.users.getPermissionCatalogue();
            this.ownPermissions = res.data?.own || {};
        },
        async loadLookups() {
            const res = await api.tkPeople.getEngagementLookups();
            this.lookups = res.data;
        },
        async loadStaff() {
            const res = await api.users.getAppStaffList();
            this.staff = res.data || [];
        },
        async loadEngagement() {
            const res = await api.tkPeople.getEngagement(this.$route.params.personId, this.$route.params.engagementId);
            this.engagement = mapEngagementRow(res.data);
            this.resetForms();
        },
        resetForms() {
            this.detailForm = {
                serviceCode: this.engagement.service_code,
                assignedWorker: this.engagement.assigned_worker,
                additionalWorkers: this.engagement.additionalWorkers.map(worker => worker.staff_id),
                referralSource: this.engagement.referral_source,
                riskFactor: this.engagement.needs_tier,
                riskTypeOptionIds: this.engagement.risk_assessments.map(risk => String(risk.id)),
                riskAssessmentDate: toDDMMYYYY(this.engagement.risk_assessment_at || new Date()),
                riskAssessmentNotes: this.engagement.risk_assessment_notes || "",
                activeReminderReviewedDate: toDDMMYYYY(this.engagement.active_reminder_reviewed_at),
                enteredDate: toDDMMYYYY(this.engagement.entered_at),
                checklistDefinitionIds: (this.engagement.checklist_items || [])
                    .filter(item => item.checklist_item_definition_id)
                    .map(item => item.checklist_item_definition_id),
                customChecklistLabels: (this.engagement.checklist_items || [])
                    .filter(item => !item.checklist_item_definition_id && item.custom_label)
                    .map(item => item.custom_label),
            };
            this.detailCustomChecklistLabel = "";
            this.lifecycleEditForm = {
                statusCode: this.engagement.status_code,
                activatedDate: toDDMMYYYY(this.engagement.activated_at),
                exitedDate: toDDMMYYYY(this.engagement.exited_at),
                exitCode: this.engagement.exit_code,
                referredTo: this.engagement.referred_to,
                signedOffBy: this.engagement.signed_off_by,
                signedOffDate: toDDMMYYYY(this.engagement.signed_off_at),
            };
        },
        async saveDetails() {
            const enteredAt = this.submitDateOrExisting(this.detailForm.enteredDate, this.engagement.entered_at);
            if (!enteredAt) {
                notify.error("Entry Date is invalid", "top");
                return false;
            }
            const riskAssessmentChanged = this.hasRiskAssessmentChanges();
            const riskAssessmentDate = this.submitDateOrExisting(
                this.detailForm.riskAssessmentDate,
                this.engagement.risk_assessment_at
            );
            if (riskAssessmentChanged && !riskAssessmentDate) {
                notify.error("Risk Assessment Date is invalid", "top");
                return false;
            }
            const riskAssessmentPayload = {
                risk_type_option_ids: [...this.detailForm.riskTypeOptionIds],
                assessed_at: riskAssessmentDate,
                notes: String(this.detailForm.riskAssessmentNotes || "").trim() || null,
            };
            const activeReminderReviewedAt = this.detailForm.activeReminderReviewedDate
                ? this.submitDateOrExisting(
                    this.detailForm.activeReminderReviewedDate,
                    this.engagement.active_reminder_reviewed_at
                )
                : null;
            if (this.detailForm.activeReminderReviewedDate && !activeReminderReviewedAt) {
                notify.error("Active Reminder Last Reviewed date is invalid", "top");
                return false;
            }

            return this.patchEngagement({
                service_code: this.detailForm.serviceCode,
                assigned_worker: this.detailForm.assignedWorker,
                additional_workers: this.additionalWorkerPayload(),
                referral_source: this.detailForm.referralSource,
                needs_tier: this.detailForm.riskFactor,
                active_reminder_reviewed_at: this.detailForm.riskFactor
                    ? activeReminderReviewedAt
                    : null,
                entered_at: enteredAt,
                checklist_items: this.checklistPayload(),
                ...(riskAssessmentChanged ? { risk_assessment: riskAssessmentPayload } : {}),
            }, "Client Engagement details saved");
        },
        hasRiskAssessmentChanges() {
            const currentIds = this.engagement.risk_assessments.map(risk => Number(risk.id)).sort((a, b) => a - b);
            const selectedIds = this.detailForm.riskTypeOptionIds.map(Number).sort((a, b) => a - b);
            if (!selectedIds.length) return currentIds.length > 0;
            const idsChanged = JSON.stringify(currentIds) !== JSON.stringify(selectedIds);
            const dateChanged = this.detailForm.riskAssessmentDate !== toDDMMYYYY(this.engagement.risk_assessment_at);
            const notesChanged = String(this.detailForm.riskAssessmentNotes || "").trim()
                !== String(this.engagement.risk_assessment_notes || "").trim();
            return idsChanged || dateChanged || notesChanged;
        },
        checklistPayload() {
            const definitionItems = this.detailForm.checklistDefinitionIds.map(id => ({ checklist_item_definition_id: id }));
            const customItems = this.detailForm.customChecklistLabels.map(label => ({ custom_label: label }));
            return [...definitionItems, ...customItems];
        },
        addDetailCustomChecklistItem() {
            const label = String(this.detailCustomChecklistLabel || "").trim();
            if (!label || this.detailForm.customChecklistLabels.includes(label)) return;
            this.detailForm.customChecklistLabels = [...this.detailForm.customChecklistLabels, label];
            this.detailCustomChecklistLabel = "";
        },
        removeDetailCustomChecklistItem(label) {
            this.detailForm.customChecklistLabels = this.detailForm.customChecklistLabels.filter(item => item !== label);
        },
        async setChecklistItemCompletion(item, isCompleted) {
            this.checklistSavingId = item.id;
            try {
                const res = await api.tkPeople.updateChecklistItemCompletion(
                    this.engagement.tk_person_id,
                    this.engagement.id,
                    item.id,
                    isCompleted
                );
                const updatedItem = res.data;
                this.engagement.checklist_items = this.engagement.checklist_items.map(checklistItem => (
                    checklistItem.id === updatedItem.id ? updatedItem : checklistItem
                ));
            } catch (error) {
                const message = error?.response?.statusText;
                notify.error(message && message !== "Internal Server Error" ? message : "Failed to update checklist item", "top");
            } finally {
                this.checklistSavingId = null;
            }
        },
        async saveLifecycle() {
            if (!this.canEditWhaioraStatus) return false;
            const statusCode = this.lifecycleEditForm.statusCode;
            const payload = { status_code: statusCode };

            if (statusCode === "active") {
                payload.activated_at = this.submitDateOrExisting(this.lifecycleEditForm.activatedDate, this.engagement.activated_at);
            }

            if (statusCode === "exited") {
                payload.activated_at = this.submitDateOrExisting(this.lifecycleEditForm.activatedDate, this.engagement.activated_at);
                payload.exited_at = this.submitDateOrExisting(this.lifecycleEditForm.exitedDate, this.engagement.exited_at);
                payload.exit_code = this.lifecycleEditForm.exitCode;
                payload.referred_to = this.lifecycleEditForm.referredTo || null;
                payload.signed_off_by = this.lifecycleEditForm.signedOffBy || null;
                payload.signed_off_at = this.lifecycleEditForm.signedOffDate
                    ? this.submitDateOrExisting(this.lifecycleEditForm.signedOffDate, this.engagement.signed_off_at)
                    : null;
            }

            return this.patchEngagement(payload, "Client Engagement status saved");
        },
        async saveDetailsDialog() {
            const valid = await this.$refs.detailsForm?.validate();
            if (!valid) return;
            const saved = await this.saveDetails();
            if (saved) this.showDetailsDialog = false;
        },
        async saveLifecycleDialog() {
            const saved = await this.saveLifecycle();
            if (saved) this.showStatusDialog = false;
        },
        async patchEngagement(payload, successMessage) {
            this.saving = true;
            try {
                const res = await api.tkPeople.updateEngagement(
                    this.engagement.tk_person_id,
                    this.engagement.id,
                    payload
                );
                this.engagement = mapEngagementRow(res.data);
                this.resetForms();
                notify.primary(successMessage, "top");
                return true;
            } catch (error) {
                const message = error?.response?.statusText;
                notify.error(message && message !== "Internal Server Error" ? message : "Failed to update Client Engagement", "top");
                return false;
            } finally {
                this.saving = false;
            }
        },
        async onCaseNoteCreated() {
            this.showCaseNoteDialog = false;
            await this.loadEngagement();
            this.$nextTick(() => {
                this.$refs.caseNotesFeed?.loadNotes();
                this.$refs.caseNotesHistory?.loadNotes();
            });
        },
        openCaseNoteHistory() {
            this.activePanel = "case-notes";
        },
        openInsights() {
            if (!this.canAccessInsights) return;
            this.activePanel = "insights";
        },
        openNewCaseNote() {
            if (!this.canCreateCaseNotes) return;
            this.editingCaseNote = null;
            this.showCaseNoteDialog = true;
        },
        openCaseNoteEdit(note) {
            if (!this.canEditCaseNotes || note.can_edit !== true) return;
            this.editingCaseNote = note;
            this.showCaseNoteDialog = true;
        },
        onCaseNoteDrawerHide() {
            this.editingCaseNote = null;
            this.caseNoteDrawerKey += 1;
        },
        async onCaseNoteUpdated() {
            this.showCaseNoteDialog = false;
            await this.loadEngagement();
            this.$nextTick(() => {
                this.$refs.caseNotesFeed?.loadNotes();
                this.$refs.caseNotesHistory?.loadNotes();
            });
        },
        onGoalsCreated() {
            this.$nextTick(() => this.$refs.engagementGoals?.refreshGoals());
        },
        onDocumentUploaded() {
            this.showDocumentDialog = false;
            this.$nextTick(() => this.$refs.attachmentsPanel?.refresh());
        },
        onPersonUpdated(person) {
            const personFields = {
                first_name: person.first_name,
                last_name: person.last_name,
                preferred_name: person.preferred_name,
                date_of_birth: person.date_of_birth,
                gender_id: person.gender_id,
                ethnicity_id: person.ethnicity_id,
                iwi_id: person.iwi_id,
                nhi: person.nhi,
                phone: person.phone,
                phone_numbers: person.phone_numbers,
                email: person.email,
                address_line_1: person.address_line_1,
                suburb: person.suburb,
                city: person.city,
                postcode: person.postcode,
                sa2_code: person.sa2_code,
                emergency_contact_name: person.emergency_contact_name,
                emergency_contact_relationship: person.emergency_contact_relationship,
                emergency_contact_phone: person.emergency_contact_phone,
                emergency_contacts: person.emergency_contacts,
                linked_paua_client_id: person.linked_paua_client_id,
                linked_paua_person_id: person.linked_paua_person_id,
            };
            this.engagement = mapEngagementRow({ ...this.engagement, ...personFields });
        },
    },
};
</script>

<style>
.tk-detail-page {
    width: 100%;
}

.tk-attendance-progress {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    min-width: 130px;
}

.tk-attendance-progress__bar {
    width: 96px;
}

.tk-attendance-progress__label {
    min-width: 4ch;
    color: #25434a;
    font-variant-numeric: tabular-nums;
    text-align: right;
}

.tk-detail-content {
    position: relative;
    width: 100%;
    min-height: calc(100vh - 64px);
    container-name: tk-engagement;
    container-type: inline-size;
    background: linear-gradient(180deg, rgba(237, 244, 239, 0.82), #ffffff 260px);
}

.tk-topbar {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 14px;
    min-height: 56px;
    padding: 10px 24px;
    background: rgba(255, 255, 255, 0.96);
    border-bottom: 1px solid rgba(47, 62, 70, 0.1);
    backdrop-filter: blur(8px);
}

.tk-topbar__left,
.tk-breadcrumb {
    display: flex;
    align-items: center;
}

.tk-topbar__left {
    gap: 10px;
    min-width: 0;
}

.tk-breadcrumb {
    gap: 4px;
    min-width: 0;
    color: #5d6b70;
    font-size: 0.92rem;
}

.tk-breadcrumb button {
    padding: 0;
    border: 0;
    background: transparent;
    color: #2f6f5e;
    cursor: pointer;
    font: inherit;
    font-weight: 550;
}

.tk-breadcrumb span {
    color: #46544e;
    font-weight: 550;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tk-page-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 16px;
    align-items: start;
    padding: 16px 24px 28px;
}

.tk-main-column,
.tk-aside-column {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
}

.tk-aside-column {
    position: sticky;
    top: 72px;
}

.tk-card,
.tk-side-card {
    min-width: 0;
    border: 1px solid rgba(47, 62, 70, 0.08);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.97);
    box-shadow: 0 8px 20px rgba(47, 62, 70, 0.05);
}

.tk-card {
    padding: 16px;
}

.tk-side-card {
    padding: 14px;
}

.tk-card-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
}

.tk-card-header--compact {
    margin-bottom: 10px;
}

.tk-card-header h2,
.tk-dialog-header h2 {
    margin: 0;
    color: #3d4b46;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 0.035em;
    line-height: 1.25;
}

.tk-card :deep(.tk-card-header h2) {
    color: #3d4b46;
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 0.035em;
    line-height: 1.25;
}

.tk-field-row span,
.tk-contact-row span,
.tk-current-status span,
.tk-timeline-item span {
    color: #748078;
    font-size: 0.8rem;
    font-weight: 450;
    letter-spacing: 0.01em;
    line-height: 1.25;
    text-transform: none;
}

.tk-field-row strong,
.tk-contact-row strong,
.tk-worker-card strong,
.tk-timeline-item strong {
    color: #3c4b45;
    font-size: 0.93rem;
    font-weight: 550;
    line-height: 1.3;
    overflow-wrap: anywhere;
}

.tk-field-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 18px;
}

.tk-field-row {
    display: grid;
    grid-template-columns: 118px minmax(0, 1fr);
    gap: 10px;
    align-items: start;
    padding: 9px 0;
    border-bottom: 1px solid rgba(47, 62, 70, 0.06);
}

.tk-current-status {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.tk-current-status span {
    text-transform: none;
}

.tk-engagement-detail-row {
    display: grid;
    grid-template-columns: 112px minmax(0, 1fr);
    gap: 8px;
    align-items: start;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(47, 62, 70, 0.07);
}

.tk-engagement-detail-row span {
    color: #748078;
    font-size: 0.8rem;
    font-weight: 450;
    letter-spacing: 0.01em;
    line-height: 1.25;
    text-transform: none;
}

.tk-engagement-detail-row strong {
    color: #3c4b45;
    font-size: 0.85rem;
    font-weight: 550;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

.tk-engagement-detail-row--last {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: 0;
}

.tk-timeline {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.tk-timeline-item {
    position: relative;
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr);
    gap: 8px;
    min-height: 42px;
    padding-bottom: 8px;
}

.tk-timeline-item::before {
    content: "";
    position: absolute;
    top: 16px;
    bottom: 0;
    left: 5px;
    width: 2px;
    background: rgba(93, 107, 112, 0.18);
}

.tk-timeline-item:last-child::before {
    display: none;
}

.tk-timeline-dot {
    width: 12px;
    height: 12px;
    margin-top: 3px;
    border-radius: 999px;
    background: #d9e2dc;
    border: 2px solid white;
    box-shadow: 0 0 0 1px rgba(47, 62, 70, 0.16);
}

.tk-timeline-item--complete .tk-timeline-dot {
    background: #4d8f77;
}

.tk-timeline-item span {
    display: block;
    margin-top: 2px;
    text-transform: none;
}

.tk-timeline-heading {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: baseline;
}

.tk-timeline-heading span {
    display: inline;
    margin-top: 0;
    color: #748078;
    font-size: 0.84rem;
    font-weight: 450;
}

.tk-worker-card,
.tk-contact-row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 10px;
    align-items: center;
}

.tk-worker-card span {
    display: block;
    color: #748078;
    font-size: 0.86rem;
    line-height: 1.3;
}

.tk-worker-group__label {
    display: block;
    margin-bottom: 8px;
    color: #748078;
    font-size: 0.8rem;
    font-weight: 500;
    letter-spacing: 0.035em;
    line-height: 1.25;
    text-transform: uppercase;
}

.tk-contact-rows {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.tk-contact-row {
    align-items: baseline;
}

.tk-contact-row strong {
    display: block;
}

.tk-contact-row a,
.tk-field-row a {
    color: #2f6f5e;
    font-size: 0.85rem;
    font-weight: 550;
    line-height: 1.26;
    overflow-wrap: anywhere;
    text-decoration: none;
}

.tk-contact-row a:hover,
.tk-field-row a:hover {
    text-decoration: underline;
}

.tk-checklist-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.tk-checklist-list :deep(.q-checkbox__label) {
    color: #46544e;
    font-size: 0.9rem;
    font-weight: 450;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

.tk-checklist-empty {
    color: #748078;
    font-size: 0.88rem;
}

.tk-form-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    align-items: start;
}

.tk-form-grid--status {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.tk-field {
    min-width: 0;
}

.tk-field--wide {
    grid-column: 1 / -1;
}

.tk-field--half {
    grid-column: span 2;
}

.tk-field p {
    margin: 0 0 6px;
    color: #68756e;
    font-size: 0.9rem;
}

.tk-field p strong {
    font-weight: 550;
}

.tk-field p strong::after {
    content: ":";
}

.tk-dialog-card {
    width: min(1200px, 96vw);
    overflow-x: hidden;
}

.tk-dialog-card--wide {
    width: min(1200px, 96vw);
}

.tk-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding-bottom: 8px;
}

.tk-dialog-checklist {
    margin-top: 16px;
}

.tk-dialog-checklist p {
    margin: 0 0 6px;
    color: #2f3e46;
    font-size: 0.9rem;
}

.tk-dialog-checklist p strong::after {
    content: ":";
}

.tk-custom-checklist {
    align-items: center;
    margin-top: 10px;
}

.tk-custom-chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

.tk-empty-state {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    margin: 24px;
    padding: 20px;
    border-radius: 8px;
    border: 1px solid rgba(47, 62, 70, 0.08);
    background: white;
    color: #5d6b70;
}

.tk-empty-state h2 {
    margin: 0 0 4px;
    color: #25434a;
    font-size: 1.15rem;
}

.tk-empty-state p {
    margin: 0;
}

@media (max-width: 1050px) {
    .tk-page-grid {
        grid-template-columns: 1fr;
    }

    .tk-aside-column {
        position: static;
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 760px) {
    .tk-topbar {
        align-items: flex-start;
        flex-direction: column;
    }

    .tk-topbar {
        padding: 10px 12px;
    }

    .tk-page-grid {
        padding: 12px;
    }

    .tk-field-grid,
    .tk-form-grid,
    .tk-form-grid--status,
    .tk-aside-column {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 560px) {
    .tk-field-row {
        grid-template-columns: 1fr;
        gap: 2px;
    }
}

@container tk-engagement (max-width: 1050px) {
    .tk-page-grid {
        grid-template-columns: 1fr;
    }

    .tk-aside-column {
        position: static;
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@container tk-engagement (max-width: 760px) {
    .tk-topbar {
        align-items: flex-start;
        flex-direction: column;
    }

    .tk-topbar {
        padding: 10px 12px;
    }

    .tk-page-grid {
        padding: 12px;
    }

    .tk-field-grid,
    .tk-form-grid,
    .tk-form-grid--status,
    .tk-aside-column {
        grid-template-columns: 1fr;
    }
}

@container tk-engagement (max-width: 560px) {
    .tk-field-row {
        grid-template-columns: 1fr;
        gap: 2px;
    }
}
</style>
